import React, { useState, useEffect } from "react";
import dayjs from "dayjs";

// material-ui
import { useTheme } from "@mui/material/styles";
import Stack from "@mui/material/Stack";
import Typography from "@mui/material/Typography";
import Box from "@mui/material/Box";
import Divider from "@mui/material/Divider";
import IconButton from "@mui/material/IconButton";

// project import
import { OutlineButton } from "components/buttons/OutlineButton";
import { SolidButton } from "components/buttons/SolidButton";
import ModalContainer from "components/ModalContainer";

import CaretRightFilled from "@ant-design/icons/CaretRightFilled";
import WarningFilled from "@ant-design/icons/WarningFilled";
import CloseOutlined from "@ant-design/icons/CloseOutlined";

// local import
import CountdownOverlay from "./CountdownOverlay";

const ServiceChangeModal = ({
  open,
  handleClose,
  serviceName,
  siteName,
  devices = 1,
  isOrgLevel,
  actionType,
  rateText,
  onConfirm,
  currentDailyBurn = 0,
  balance = 0, // AI Chatbot(future)
  baseRates, // AI Chatbot(future)
  activePreConfirmData,
}) => {
  const theme = useTheme();

  // --- Internal Countdown Logic ---
  const [showCountdown, setShowCountdown] = useState(false);
  const [timeLeft, setTimeLeft] = useState(10);

  useEffect(() => {
    if (!open) {
      setShowCountdown(false);
      setTimeLeft(10);
      return;
    }
  }, [open]);

  useEffect(() => {
    if (!showCountdown || timeLeft <= 0) {
      if (showCountdown && timeLeft === 0) {
        onConfirm && onConfirm();
        setShowCountdown(false);
      }
      return;
    }

    const timer = setInterval(() => {
      setTimeLeft((prev) => prev - 1);
    }, 1000);

    return () => clearInterval(timer);
  }, [showCountdown, timeLeft, onConfirm]);

  const handleStartCountdown = () => setShowCountdown(true);
  const handleCancelCountdown = () => {
    setShowCountdown(false);
    setTimeLeft(10);
  };

  // Calculation Logic
  const today = dayjs();
  const lastDayOfMonth = today.endOf("month");
  const remainingDaysVal = Math.max(0, lastDayOfMonth.diff(today, "day")); // 排除當天

  const preConfirmItem = activePreConfirmData;
  const hasPreConfirm = !!preConfirmItem && Object.keys(preConfirmItem).length > 0;

  const serviceRatePerUnit = 1;
  const deviceCount = hasPreConfirm
    ? preConfirmItem.deviceCount
    : (isOrgLevel ? 1 : devices || 0);

  const dailyRateValue = hasPreConfirm
    ? preConfirmItem.dailyRate
    : deviceCount * serviceRatePerUnit;

  const showRemainingDays = hasPreConfirm
    ? preConfirmItem.remainingDays
    : remainingDaysVal;

  const totalCredits = hasPreConfirm
    ? preConfirmItem.dailyRate * preConfirmItem.remainingDays
    : showRemainingDays * dailyRateValue;

  const isInsufficient = hasPreConfirm && preConfirmItem.sufficientCredits === false;

  const isActivate = actionType?.toLowerCase() === "activate";
  const dailyChange = isActivate ? dailyRateValue : -dailyRateValue;
  const afterChange = currentDailyBurn + dailyChange;

  const titleColor = "#172664";
  const valueColor = "#008514";
  const actionColor = isActivate ? "#172664" : "#D1003B";

  return (
    <ModalContainer display={open} modalWidth='600px'>
      <Box sx={{ position: "relative" }}>
        {/* Internal Countdown Overlay (Covers the whole content including padding) */}
        {showCountdown && (
          <CountdownOverlay
            timeLeft={timeLeft}
            handleCancel={handleCancelCountdown}
          />
        )}

        <Stack spacing={2.5}>
          {/* Header */}
          <Stack
            direction='row'
            justifyContent='space-between'
            alignItems='center'
          >
            <Typography
              sx={{
                color: titleColor,
                fontSize: "18px",
                fontWeight: "bold",
              }}
            >
              Confirm Service Change
            </Typography>
            <IconButton onClick={handleClose} sx={{ p: 0.5 }}>
              <CloseOutlined
                style={{ fontSize: "18px", color: "rgba(0,0,0,0.45)" }}
              />
            </IconButton>
          </Stack>

          <Box sx={{ overflowY: "auto", maxHeight: "75vh" }}>
            <Stack spacing={2}>
              {/* Service Info */}
              <Stack spacing={1}>
                <Stack direction='row' alignItems='center' spacing={1}>
                  <CaretRightFilled
                    style={{ fontSize: "14px", color: "#172664" }}
                  />
                  <Typography
                    sx={{
                      color: "rgba(0,0,0,0.85)",
                      fontSize: "15px",
                      fontWeight: "bold",
                    }}
                  >
                    {isOrgLevel ? "Organization" : siteName || "Site"}
                  </Typography>
                </Stack>
                <Typography
                  sx={{
                    color: titleColor,
                    fontSize: "16px",
                    fontWeight: "bold",
                    mb: 0.5,
                  }}
                >
                  Service: {serviceName}
                </Typography>
                <Divider sx={{ mb: 1.5 }} />

                <Stack spacing={0.5}>
                  <Stack direction='row' spacing={1}>
                    <Typography variant='body2' sx={{ minWidth: "65px" }}>
                      Action:
                    </Typography>
                    <Typography
                      variant='body2'
                      sx={{ color: actionColor, fontWeight: "bold" }}
                    >
                      {actionType}
                    </Typography>
                  </Stack>
                  <Stack direction='row' spacing={1}>
                    <Typography variant='body2' sx={{ minWidth: "65px" }}>
                      Devices:
                    </Typography>
                    <Typography
                      variant='body2'
                      sx={{ color: "#373737", fontWeight: "bold" }}
                    >
                      <Box component='span' sx={{ color: valueColor }}>
                        {deviceCount}
                      </Box>
                    </Typography>
                  </Stack>
                  <Stack direction='row' spacing={1}>
                    <Typography variant='body2' sx={{ minWidth: "65px" }}>
                      Rate:
                    </Typography>
                    <Typography
                      variant='body2'
                      sx={{ color: "#373737", fontWeight: "bold" }}
                    >
                      {(() => {
                        const match = (rateText || "").match(
                          /^([\d.]+)\s*(.*)$/,
                        );
                        if (!match) return rateText;
                        return (
                          <>
                            <Box component='span' sx={{ color: valueColor }}>
                              {match[1]}
                            </Box>{" "}
                            {match[2]}
                          </>
                        );
                      })()}
                    </Typography>
                  </Stack>
                </Stack>
              </Stack>

              {/* Credit Section Block */}
              <Box
                sx={{
                  p: 2,
                  border: `1px solid rgba(0,0,0,0.15)`,
                  borderRadius: "12px",
                }}
              >
                <Typography
                  sx={{
                    color: titleColor,
                    fontSize: "14px",
                    fontWeight: "bold",
                    mb: 2,
                  }}
                >
                  {isActivate ? "Credit Deduction" : "Credit Refund"}
                </Typography>
                <Stack spacing={1.5}>
                  <Stack direction='row' justifyContent='space-between'>
                    <Typography variant='body2' color='textSecondary'>
                      Daily Rate
                    </Typography>
                    <Typography
                      variant='body2'
                      sx={{ fontWeight: "bold", color: "#373737" }}
                    >
                      <Box component='span' sx={{ color: valueColor }}>
                        {dailyRateValue.toLocaleString()}
                      </Box>{" "}
                      Credits / day
                    </Typography>
                  </Stack>
                  <Stack direction='row' justifyContent='space-between'>
                    <Typography variant='body2' color='textSecondary'>
                      Days
                    </Typography>
                    <Typography
                      variant='body2'
                      sx={{ fontWeight: "bold", color: "#373737" }}
                    >
                      <Box component='span' sx={{ color: valueColor }}>
                        {showRemainingDays}
                      </Box>{" "}
                      Days
                    </Typography>
                  </Stack>
                  <Stack direction='row' justifyContent='space-between'>
                    <Typography variant='body2' color='textSecondary'>
                      Total
                    </Typography>
                    <Typography
                      variant='body2'
                      sx={{ fontWeight: "bold", color: "#373737" }}
                    >
                      <Box component='span' sx={{ color: valueColor }}>
                        {totalCredits.toLocaleString()}
                      </Box>{" "}
                      Credits
                    </Typography>
                  </Stack>
                </Stack>
              </Box>

              {/* Daily Burn Rate Impact Block */}
              {/* <Box
                sx={{
                  p: 2,
                  border: `1px solid rgba(0,0,0,0.15)`,
                  borderRadius: "12px",
                }}
              >
                <Typography
                  sx={{
                    color: titleColor,
                    fontSize: "14px",
                    fontWeight: "bold",
                    mb: 2,
                  }}
                >
                  Daily Burn Rate Impact
                </Typography>
                <Stack spacing={1.5}>
                  <Stack direction='row' justifyContent='space-between'>
                    <Typography variant='body2' color='textSecondary'>
                      Current Daily Usage
                    </Typography>
                    <Typography
                      variant='body2'
                      sx={{ fontWeight: "bold", color: "#373737" }}
                    >
                      <Box component='span' sx={{ color: valueColor }}>
                        {currentDailyBurn.toLocaleString()}
                      </Box>{" "}
                      Credits / day
                    </Typography>
                  </Stack>
                  <Stack direction='row' justifyContent='space-between'>
                    <Typography variant='body2' color='textSecondary'>
                      After Change
                    </Typography>
                    <Typography
                      variant='body2'
                      sx={{ fontWeight: "bold", color: "#373737" }}
                    >
                      <Box component='span' sx={{ color: valueColor }}>
                        {afterChange.toLocaleString()}
                      </Box>{" "}
                      Credits / day
                    </Typography>
                  </Stack>
                  <Stack direction='row' justifyContent='space-between'>
                    <Typography variant='body2' color='textSecondary'>
                      Daily Change
                    </Typography>
                    <Typography
                      variant='body2'
                      sx={{ fontWeight: "bold", color: "#373737" }}
                    >
                      <Box component='span' sx={{ color: valueColor }}>
                        {isActivate ? "+" : "-"}
                        {Math.abs(dailyChange).toLocaleString()}
                      </Box>{" "}
                      Credits / day
                    </Typography>
                  </Stack>
                </Stack>
              </Box> */}

              {/* Warning Section */}
              <Stack
                direction='row'
                spacing={1}
                alignItems='center'
                sx={{ mt: 1, alignItems: "flex-start" }}
              >
                <WarningFilled style={{ color: "#D1003B", fontSize: "18px" }} />
                <Typography
                  variant='body2'
                  sx={{ color: "rgba(0,0,0,0.85)", fontWeight: "bold" }}
                >
                  {isInsufficient
                    ? "Insufficient credits. Please contact your Organization Super Admin."
                    : "Changes take effect immediately upon confirmation. Credits will be auto-deducted monthly based on your subscription plan."}
                </Typography>
              </Stack>
            </Stack>
          </Box>

          {/* Actions */}
          <Stack
            direction='row'
            justifyContent='center'
            spacing={2}
            sx={{ mt: 1 }}
          >
            <OutlineButton
              onClick={handleClose}
              width='120px'
              height='36px'
              sx={{ borderRadius: "20px" }}
            >
              Cancel
            </OutlineButton>
            <SolidButton
              onClick={handleStartCountdown}
              disabled={isInsufficient}
              width='120px'
              height='36px'
              sx={{ borderRadius: "20px", bgcolor: "#172664" }}
            >
              Confirm
            </SolidButton>
          </Stack>
        </Stack>
      </Box>
    </ModalContainer>
  );
};

export default ServiceChangeModal;
